import React, { useState, useEffect } from 'react'
import { apiClient } from '@/utils/api'
import {
  CheckCircle,
  ShoppingCart,
  Utensils,
  Gift,
  BarChart3,
  Calendar,
  GraduationCap,
  Loader2,
  AlertCircle,
  Trophy,
  Monitor,
  LayoutDashboard
} from 'lucide-react'

interface FamilyFeature {
  id: string
  feature_key: string
  enabled: boolean
  display_name: string
  description: string
  category: string
}

// Features that families can control (excludes platform-only features like greenlight, custody_schedule, kiosk_home_assistant)
const FAMILY_CONTROLLABLE_FEATURES = [
  'chore_verification',
  'chore_bidding',
  'meal_planning',
  'redemption_queue',
  'performance_analytics',
  'calendar_integration',
  'school_integration',
  'leaderboard_rewards',
  'legacy_view',
  'kiosk_chores',
  'kiosk_leaderboard',
  'kiosk_calendar',
  'kiosk_meals',
  'kiosk_weather',
  'kiosk_announcements'
]

const FEATURE_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  chore_verification: CheckCircle,
  chore_bidding: ShoppingCart,
  meal_planning: Utensils,
  redemption_queue: Gift,
  performance_analytics: BarChart3,
  calendar_integration: Calendar,
  school_integration: GraduationCap,
  leaderboard_rewards: Trophy,
  legacy_view: LayoutDashboard,
  kiosk_chores: Monitor,
  kiosk_leaderboard: Monitor,
  kiosk_calendar: Monitor,
  kiosk_meals: Monitor,
  kiosk_weather: Monitor,
  kiosk_announcements: Monitor
}

// Most family DBs have no legacy_view toggle row, and the API only lists
// existing rows. A missing row reads as disabled (useFamilyFeatures), and the
// PUT upserts, so show it as off. Metadata mirrors the backend's
// @feature_metadata in FamilyController.
const LEGACY_VIEW_DEFAULT: FamilyFeature = {
  id: 'legacy_view',
  feature_key: 'legacy_view',
  enabled: false,
  display_name: 'Classic dashboard switch',
  description: 'Let parents switch the parent dashboard between the Classic and Tiles layouts',
  category: 'family'
}

const CATEGORY_LABELS: Record<string, string> = {
  workflow: 'Chore Workflow',
  marketplace: 'Marketplace',
  family: 'Family Features',
  rewards: 'Rewards',
  analytics: 'Analytics & Reports',
  calendar: 'Calendar',
  education: 'Education',
  kiosk: 'Kiosk Display'
}

export const FamilyFeaturesManager: React.FC = () => {
  const [features, setFeatures] = useState<FamilyFeature[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [updating, setUpdating] = useState<string | null>(null)

  useEffect(() => {
    fetchFeatures()
  }, [])

  const fetchFeatures = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await apiClient.get('/api/family/features')
      if (response.success) {
        // Filter to only show family-controllable features
        const controllableFeatures: FamilyFeature[] = response.data.filter(
          (f: FamilyFeature) => FAMILY_CONTROLLABLE_FEATURES.includes(f.feature_key)
        )
        if (!controllableFeatures.some(f => f.feature_key === 'legacy_view')) {
          controllableFeatures.push(LEGACY_VIEW_DEFAULT)
        }
        setFeatures(controllableFeatures)
      } else {
        setError(response.error || 'Failed to load features')
      }
    } catch (err) {
      setError('Failed to load features')
      console.error('Error fetching features:', err)
    } finally {
      setLoading(false)
    }
  }

  const toggleFeature = async (featureKey: string, currentEnabled: boolean) => {
    try {
      setUpdating(featureKey)
      setError(null)

      // The PUT returns {feature_key, enabled} with no `success` flag; a
      // non-2xx response throws, so reaching here means it was saved.
      const response = await apiClient.put(`/api/family/features/${featureKey}`, {
        enabled: !currentEnabled
      })
      const enabled = typeof response?.enabled === 'boolean' ? response.enabled : !currentEnabled

      setFeatures(prev =>
        prev.map(f =>
          f.feature_key === featureKey
            ? { ...f, enabled }
            : f
        )
      )
    } catch (err) {
      setError('Failed to update feature')
      console.error('Error updating feature:', err)
    } finally {
      setUpdating(null)
    }
  }

  // Group features by category
  const groupedFeatures = features.reduce((acc, feature) => {
    const category = feature.category || 'other'
    if (!acc[category]) {
      acc[category] = []
    }
    acc[category].push(feature)
    return acc
  }, {} as Record<string, FamilyFeature[]>)

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        <span className="ml-2 text-gray-600">Loading features...</span>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">Family Features</h3>
        <p className="text-sm text-gray-500 mt-1">
          Enable or disable features for your family. Some features may require a page refresh to take effect.
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-red-800">Error</p>
            <p className="text-sm text-red-600">{error}</p>
          </div>
        </div>
      )}

      {Object.entries(groupedFeatures).map(([category, categoryFeatures]) => (
        <div key={category} className="space-y-3">
          <h4 className="text-sm font-medium text-gray-700 uppercase tracking-wider">
            {CATEGORY_LABELS[category] || category}
          </h4>

          <div className="bg-white border border-gray-200 rounded-lg divide-y divide-gray-100">
            {categoryFeatures.map(feature => {
              const Icon = FEATURE_ICONS[feature.feature_key] || CheckCircle
              const isUpdating = updating === feature.feature_key

              return (
                <div
                  key={feature.id}
                  className="p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-lg ${feature.enabled ? 'bg-blue-100' : 'bg-gray-100'}`}>
                      <Icon className={`w-5 h-5 ${feature.enabled ? 'text-blue-600' : 'text-gray-400'}`} />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{feature.display_name}</p>
                      <p className="text-sm text-gray-500">{feature.description}</p>
                    </div>
                  </div>

                  <button
                    onClick={() => toggleFeature(feature.feature_key, feature.enabled)}
                    disabled={isUpdating}
                    className={`
                      relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent
                      transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
                      ${feature.enabled ? 'bg-blue-600' : 'bg-gray-200'}
                      ${isUpdating ? 'opacity-50 cursor-not-allowed' : ''}
                    `}
                  >
                    <span className="sr-only">Toggle {feature.display_name}</span>
                    <span
                      className={`
                        pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0
                        transition duration-200 ease-in-out
                        ${feature.enabled ? 'translate-x-5' : 'translate-x-0'}
                      `}
                    >
                      {isUpdating && (
                        <Loader2 className="w-3 h-3 animate-spin text-gray-400 absolute top-1 left-1" />
                      )}
                    </span>
                  </button>
                </div>
              )
            })}
          </div>
        </div>
      ))}

      {features.length === 0 && !loading && (
        <div className="text-center py-8 text-gray-500">
          <p>No configurable features available.</p>
        </div>
      )}
    </div>
  )
}
